import type { RefrigerationUnitRow, RefrigerationUnitSafetyThresholdRow } from '../../db';
import { RefrigerationUnitSafety, RefrigerationUnitSafetyThreshold } from '../../schemas';
import { newId } from '../../../utils/ids';
import { trim, toNonNegativeInt, toNonNegativeNumber } from '../repoUtils';
import type { RefrigerationUnitSafetyInput, RefrigerationUnitSafetyThresholdInput } from './types';

function normalizeRefrigerationUnitThresholds(
  input:
    | RefrigerationUnitSafetyThresholdInput[]
    | RefrigerationUnitSafetyThresholdRow[]
    | undefined,
  existing: RefrigerationUnitSafetyThresholdRow[] | undefined
): RefrigerationUnitSafetyThresholdRow[] {
  const source = input ?? existing ?? [];
  return source.map((threshold) => {
    const thresholdType =
      (threshold as RefrigerationUnitSafetyThresholdInput).thresholdType ??
      threshold.thresholdType ??
      'tossicita';
    const unit = (threshold as RefrigerationUnitSafetyThresholdInput).unit ?? threshold.unit;
    const value =
      toNonNegativeNumber(
        (threshold as RefrigerationUnitSafetyThresholdInput).value ?? threshold.value
      ) ?? 0;
    const actionsSource =
      (threshold as RefrigerationUnitSafetyThresholdInput).actions ?? threshold.actions;
    const actions = (actionsSource ?? [])
      .map((action) => trim(action) ?? '')
      .filter((action): action is string => action.length > 0);
    const parsed = RefrigerationUnitSafetyThreshold.parse({
      id: threshold.id ?? newId(),
      thresholdType,
      unit,
      value,
      actions,
    });
    return {
      id: parsed.id,
      thresholdType: parsed.thresholdType,
      unit: parsed.unit,
      value: parsed.value,
      actions: parsed.actions,
    } satisfies RefrigerationUnitSafetyThresholdRow;
  });
}

export function normalizeRefrigerationUnitSafety(
  input: RefrigerationUnitSafetyInput | RefrigerationUnitRow['technical']['safety'] | undefined,
  existing: RefrigerationUnitRow['technical']['safety'] | undefined
): RefrigerationUnitRow['technical']['safety'] {
  const optionalInt = (value: number | string | null | undefined): number | undefined => {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }
    return toNonNegativeInt(value, 0);
  };

  const detectorsInput = input?.detectors ?? existing?.detectors;
  const thresholds = normalizeRefrigerationUnitThresholds(
    input?.detectors?.thresholds,
    existing?.detectors?.thresholds
  );
  const detectors = {
    toxicCount: toNonNegativeInt(detectorsInput?.toxicCount, 0),
    explosiveCount: toNonNegativeInt(detectorsInput?.explosiveCount, 0),
    positions: (detectorsInput?.positions ?? existing?.detectors?.positions ?? [])
      .map((position) => trim(position))
      .filter((position): position is string => Boolean(position)),
    thresholds,
    calibrationNotes: trim(
      detectorsInput?.calibrationNotes ?? existing?.detectors?.calibrationNotes
    ),
    ceilingCount: optionalInt(
      (detectorsInput as { ceilingCount?: number | string } | undefined)?.ceilingCount ??
        (existing?.detectors as { ceilingCount?: number | string } | undefined)?.ceilingCount
    ),
    humanHeightCount: optionalInt(
      (detectorsInput as { humanHeightCount?: number | string } | undefined)?.humanHeightCount ??
        (existing?.detectors as { humanHeightCount?: number | string } | undefined)
          ?.humanHeightCount
    ),
  };

  const ventilationSource = input?.ventilation ?? existing?.ventilation;
  const ventilation = ventilationSource
    ? {
        type: trim(ventilationSource.type ?? existing?.ventilation?.type),
        flowRateM3h: toNonNegativeNumber(
          ventilationSource.flowRateM3h ?? existing?.ventilation?.flowRateM3h
        ),
        notes: trim(ventilationSource.notes ?? existing?.ventilation?.notes),
        naturalPresent:
          ventilationSource.naturalPresent ??
          existing?.ventilation?.naturalPresent ??
          (ventilationSource.natural || existing?.ventilation?.natural ? true : undefined),
        apertureHighM2: toNonNegativeNumber(
          ventilationSource.apertureHighM2 ??
            ventilationSource.natural?.highOpeningsAreaM2 ??
            existing?.ventilation?.apertureHighM2 ??
            existing?.ventilation?.natural?.highOpeningsAreaM2
        ),
        apertureLowM2: toNonNegativeNumber(
          ventilationSource.apertureLowM2 ??
            ventilationSource.natural?.lowOpeningsAreaM2 ??
            existing?.ventilation?.apertureLowM2 ??
            existing?.ventilation?.natural?.lowOpeningsAreaM2
        ),
        natural: (() => {
          const naturalSource = ventilationSource.natural ?? existing?.ventilation?.natural;
          if (!naturalSource) return undefined;
          const high = toNonNegativeNumber(
            naturalSource.highOpeningsAreaM2 ?? existing?.ventilation?.natural?.highOpeningsAreaM2
          );
          const low = toNonNegativeNumber(
            naturalSource.lowOpeningsAreaM2 ?? existing?.ventilation?.natural?.lowOpeningsAreaM2
          );
          const notes = trim(naturalSource.notes ?? existing?.ventilation?.natural?.notes);
          if (high === undefined && low === undefined && !notes) return undefined;
          return {
            highOpeningsAreaM2: high,
            lowOpeningsAreaM2: low,
            notes,
          };
        })(),
        extractors: (() => {
          const extractorsSource =
            ventilationSource.extractors ?? existing?.ventilation?.extractors;
          if (!extractorsSource) return undefined;
          const units = optionalInt(extractorsSource.units);
          const airflowM3h = toNonNegativeNumber(extractorsSource.airflowM3h);
          const atex = extractorsSource.atex ?? existing?.ventilation?.extractors?.atex;
          const logic = trim(extractorsSource.logic);
          const mode = trim(extractorsSource.mode);
          if (
            units === undefined &&
            airflowM3h === undefined &&
            atex === undefined &&
            !logic &&
            !mode
          )
            return undefined;
          return {
            units,
            airflowM3h,
            atex,
            logic,
            mode,
          };
        })(),
        extractorsCount: optionalInt(ventilationSource.extractorsCount),
        extractorsFlowM3h: toNonNegativeNumber(ventilationSource.extractorsFlowM3h),
        extractorsOperation: trim(ventilationSource.extractorsOperation),
        extractorsAtex: ventilationSource.extractorsAtex ?? existing?.ventilation?.extractorsAtex,
      }
    : undefined;

  const scrubberSource = input?.scrubber ?? existing?.scrubber;
  const scrubber = scrubberSource
    ? {
        present: scrubberSource.present ?? existing?.scrubber?.present,
        notes: trim(scrubberSource.notes ?? existing?.scrubber?.notes),
        airFlowM3h: toNonNegativeNumber(
          scrubberSource.airFlowM3h ?? existing?.scrubber?.airFlowM3h
        ),
        waterFlowM3h: toNonNegativeNumber(
          scrubberSource.waterFlowM3h ?? existing?.scrubber?.waterFlowM3h
        ),
        waterFlowSpec: trim(scrubberSource.waterFlowSpec ?? existing?.scrubber?.waterFlowSpec),
        contactTimeSeconds: toNonNegativeNumber(
          scrubberSource.contactTimeSeconds ?? existing?.scrubber?.contactTimeSeconds
        ),
        waterSupply: trim(scrubberSource.waterSupply ?? existing?.scrubber?.waterSupply),
        wasteCollection: trim(
          scrubberSource.wasteCollection ?? existing?.scrubber?.wasteCollection
        ),
        atex: scrubberSource.atex ?? existing?.scrubber?.atex,
      }
    : undefined;

  const abatementSystemSource = input?.abatementSystem ?? existing?.abatementSystem;
  const abatementSystem = abatementSystemSource
    ? {
        present: abatementSystemSource.present ?? existing?.abatementSystem?.present,
        controlMode: abatementSystemSource.controlMode ?? existing?.abatementSystem?.controlMode,
        trim: abatementSystemSource.trim ?? existing?.abatementSystem?.trim,
        limitSwitches:
          abatementSystemSource.limitSwitches ?? existing?.abatementSystem?.limitSwitches,
        type: trim(abatementSystemSource.type ?? existing?.abatementSystem?.type),
        supply: trim(abatementSystemSource.supply ?? existing?.abatementSystem?.supply),
        disposal: trim(abatementSystemSource.disposal ?? existing?.abatementSystem?.disposal),
        waterFlowM3h: toNonNegativeNumber(
          abatementSystemSource.waterFlowM3h ?? existing?.abatementSystem?.waterFlowM3h
        ),
      }
    : undefined;

  const powerBackupSource = input?.powerBackup ?? existing?.powerBackup;
  const powerBackup = powerBackupSource
    ? {
        preferredLine: powerBackupSource.preferredLine ?? existing?.powerBackup?.preferredLine,
        generator: powerBackupSource.generator ?? existing?.powerBackup?.generator,
        ups: powerBackupSource.ups ?? existing?.powerBackup?.ups,
        notes: trim(powerBackupSource.notes ?? existing?.powerBackup?.notes),
      }
    : undefined;

  const emergencySystemsSource = input?.emergencySystems ?? existing?.emergencySystems;
  const emergencySystems = emergencySystemsSource
    ? {
        dialerPresent:
          emergencySystemsSource.dialerPresent ?? existing?.emergencySystems?.dialerPresent,
        guardServicePresent:
          emergencySystemsSource.guardServicePresent ??
          existing?.emergencySystems?.guardServicePresent,
        generatorPresent:
          emergencySystemsSource.generatorPresent ?? existing?.emergencySystems?.generatorPresent,
        notes: trim(emergencySystemsSource.notes ?? existing?.emergencySystems?.notes),
        eeTripButtons:
          emergencySystemsSource.eeTripButtons ?? existing?.emergencySystems?.eeTripButtons,
        eeTripDetector:
          emergencySystemsSource.eeTripDetector ?? existing?.emergencySystems?.eeTripDetector,
        elettroserraturaPresent:
          emergencySystemsSource.elettroserraturaPresent ??
          existing?.emergencySystems?.elettroserraturaPresent,
        emergencyLightingPresent:
          emergencySystemsSource.emergencyLightingPresent ??
          existing?.emergencySystems?.emergencyLightingPresent,
        emergencyLightingAtex:
          emergencySystemsSource.emergencyLightingAtex ??
          existing?.emergencySystems?.emergencyLightingAtex,
        sgancioAutomaticoPresent:
          emergencySystemsSource.sgancioAutomaticoPresent ??
          existing?.emergencySystems?.sgancioAutomaticoPresent,
        sogliaSgancioAutomatico: trim(
          emergencySystemsSource.sogliaSgancioAutomatico ??
            existing?.emergencySystems?.sogliaSgancioAutomatico
        ),
        pulsanteSgancioPresent:
          emergencySystemsSource.pulsanteSgancioPresent ??
          existing?.emergencySystems?.pulsanteSgancioPresent,
      }
    : undefined;

  const valvesSource = input?.valves ?? existing?.valves;
  const valves = valvesSource
    ? {
        reliefSetPressuresBar:
          valvesSource.reliefSetPressuresBar ?? existing?.valves?.reliefSetPressuresBar,
        reliefPeriodicityYears: optionalInt(
          valvesSource.reliefPeriodicityYears ?? existing?.valves?.reliefPeriodicityYears
        ),
        reliefNotes: trim(valvesSource.reliefNotes ?? existing?.valves?.reliefNotes),
        isolationPresent: valvesSource.isolationPresent ?? existing?.valves?.isolationPresent,
        isolationDiverter: valvesSource.isolationDiverter ?? existing?.valves?.isolationDiverter,
        isolationNotes: trim(valvesSource.isolationNotes ?? existing?.valves?.isolationNotes),
      }
    : undefined;

  const detectionMaintenanceSource = input?.detectionMaintenance ?? existing?.detectionMaintenance;
  const detectionMaintenance = detectionMaintenanceSource
    ? {
        name: trim(detectionMaintenanceSource.name ?? existing?.detectionMaintenance?.name),
        notes: trim(detectionMaintenanceSource.notes ?? existing?.detectionMaintenance?.notes),
      }
    : undefined;

  const vsReliefSource = input?.vsRelief ?? existing?.vsRelief;
  const vsRelief = vsReliefSource
    ? {
        collectorsCount: optionalInt(
          vsReliefSource.collectorsCount ?? existing?.vsRelief?.collectorsCount
        ),
        sizing: trim(vsReliefSource.sizing ?? existing?.vsRelief?.sizing),
      }
    : undefined;

  const abatementTankSource = input?.abatementTank ?? existing?.abatementTank;
  const abatementTank = abatementTankSource
    ? {
        volumeMc: toNonNegativeNumber(abatementTankSource.volumeMc),
        minWaterMc: toNonNegativeNumber(abatementTankSource.minWaterMc),
        minFreeMc: toNonNegativeNumber(abatementTankSource.minFreeMc),
        location: abatementTankSource.location ?? existing?.abatementTank?.location,
        signage: abatementTankSource.signage ?? existing?.abatementTank?.signage,
        accessibility: abatementTankSource.accessibility ?? existing?.abatementTank?.accessibility,
        material: abatementTankSource.material ?? existing?.abatementTank?.material,
        dimensions: trim(abatementTankSource.dimensions ?? existing?.abatementTank?.dimensions),
        orientation: abatementTankSource.orientation ?? existing?.abatementTank?.orientation,
        levelInstrumentation:
          abatementTankSource.levelInstrumentation ?? existing?.abatementTank?.levelInstrumentation,
        continuousPhMonitoring:
          abatementTankSource.continuousPhMonitoring ??
          existing?.abatementTank?.continuousPhMonitoring,
        emergencySupply:
          abatementTankSource.emergencySupply ?? existing?.abatementTank?.emergencySupply,
      }
    : undefined;

  const vvfDocsSource = input?.vvfDocs ?? existing?.vvfDocs;
  const vvfDocsLocationSource = input?.vvfDocsLocation ?? existing?.vvfDocsLocation;
  const vvfDocs = typeof vvfDocsSource === 'string' ? vvfDocsSource : undefined;
  const vvfDocsLocation = trim(
    typeof vvfDocsLocationSource === 'string' ? vvfDocsLocationSource : undefined
  );

  return RefrigerationUnitSafety.parse({
    detectors,
    ventilation,
    scrubber,
    abatementSystem,
    powerBackup,
    emergencySystems,
    valves,
    detectionMaintenance,
    vsRelief,
    abatementTank,
    vvfDocs,
    vvfDocsLocation,
  });
}
